import { useState } from 'react';
import { Button, Box, Typography, IconButton, Tooltip, useTheme } from '@mui/material';
import DataTable from 'components/Common/DataTable';
import { ColumnDef } from '@tanstack/react-table';
import { GoPlus } from 'react-icons/go';
import { MdOutlineEdit } from 'react-icons/md';
import { RiDeleteBin6Line } from 'react-icons/ri';
import { Link, Outlet, useNavigate } from 'react-router-dom';
import { useDeleteAgentAction, useGetAgentActions } from 'apis/business/agent/action/hook';
import { AgentActionResponse } from 'apis/business/agent/action/types';
import Pagination from 'components/Common/Pagination';

export default function Actions() {
  const navigate = useNavigate();
  const theme = useTheme();
  const [currentPage, setCurrentPage] = useState(1);
  const { data: agentActions, isLoading: isAgentActionsLoading } = useGetAgentActions(currentPage);

  const { mutate: deleteAgentAction } = useDeleteAgentAction();

  return (
    <>
      <Box display='flex' justifyContent='space-between' alignItems='center' mb={2}>
        <Typography variant='h2' fontWeight={500}>
          Actions
        </Typography>
        <Button
          variant='contained'
          color='primary'
          startIcon={<GoPlus />}
          sx={{ textTransform: 'none' }}
          onClick={() => navigate('add')}
        >
          Add
        </Button>
      </Box>

      <DataTable
        data={agentActions?.data}
        columns={columns(theme, deleteAgentAction)}
        isHeaderSticky={true}
        isLoading={isAgentActionsLoading}
      />
      <Pagination totalPages={agentActions?.meta?.total_pages} currentPage={currentPage} onChange={setCurrentPage} />
      <Outlet />
    </>
  );
}

const columns = (theme: any, deleteAgentAction: (id: string) => void): ColumnDef<AgentActionResponse>[] => [
  {
    header: 'Name',
    accessorKey: 'name'
  },
  {
    header: 'Type',
    accessorKey: 'action_type',
    cell: (info) => (info.getValue() as string)?.replace(/_/g, ' ')
  },
  {
    header: 'Trigger',
    accessorKey: 'action_details.trigger',
    cell: (info) => {
      const value = info.getValue() as string;
      if (value?.length > 20) {
        return (
          <>
            {value.slice(0, 20)}..
            <Tooltip title={value}>
              <Typography component='span' style={{ cursor: 'pointer', color: theme.palette.primary.main }}>
                See More
              </Typography>
            </Tooltip>
          </>
        );
      } else {
        return value;
      }
    }
  },
  {
    header: 'Action',
    cell: (info) => (
      <Box display='flex' gap={1}>
        <Link to={String(info.row.original.id)}>
          <IconButton>
            <MdOutlineEdit />
          </IconButton>
        </Link>
        <IconButton color='error' onClick={() => deleteAgentAction(String(info.row.original.id))}>
          <RiDeleteBin6Line style={{ color: 'red' }} />
        </IconButton>
      </Box>
    )
  }
];
